import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarPlus, Pencil, X } from 'lucide-react';
import { getCalendarAvailability } from '../../services/ownerApi.js';

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const AVAILABILITY_FETCH_MAX_DAYS = 62;

function formatDateKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDaysYmd(ymd, days) {
  if (!YMD_PATTERN.test(ymd || '')) return '';
  const [year, month, day] = ymd.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return formatDateKey(date);
}

function dateDiffNights(startYmd, endYmd) {
  if (!YMD_PATTERN.test(startYmd || '') || !YMD_PATTERN.test(endYmd || '')) return 0;
  const [sy, sm, sd] = startYmd.split('-').map(Number);
  const [ey, em, ed] = endYmd.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const ms = end.getTime() - start.getTime();
  return ms > 0 ? Math.round(ms / (1000 * 60 * 60 * 24)) : 0;
}

function getStayNightKeys(startYmd, endYmd) {
  const nights = dateDiffNights(startYmd, endYmd);
  if (nights <= 0) return [];

  return Array.from({ length: nights }, (_, index) => addDaysYmd(startYmd, index));
}

function getNightlyRate(dayData) {
  if (!dayData || typeof dayData !== 'object') return null;
  const rawRate = dayData.nightly_rate ?? dayData.price ?? dayData.rate ?? null;
  const rate = typeof rawRate === 'string' ? Number.parseFloat(rawRate) : Number(rawRate);
  return Number.isFinite(rate) && rate >= 0 ? rate : null;
}

function buildAvailabilityChunks(startYmd, endYmd) {
  if (!YMD_PATTERN.test(startYmd || '') || !YMD_PATTERN.test(endYmd || '') || startYmd > endYmd) {
    return [];
  }

  const chunks = [];
  let cursor = startYmd;

  while (cursor && cursor <= endYmd) {
    const maxChunkEnd = addDaysYmd(cursor, AVAILABILITY_FETCH_MAX_DAYS - 1);
    const chunkEnd = maxChunkEnd && maxChunkEnd < endYmd ? maxChunkEnd : endYmd;
    chunks.push({ start: cursor, end: chunkEnd });
    cursor = addDaysYmd(chunkEnd, 1);
  }

  return chunks;
}

async function fetchAvailabilityForStay({ listingId, startYmd, endYmd }) {
  const chunks = buildAvailabilityChunks(startYmd, endYmd);
  const days = {};

  for (const chunk of chunks) {
    const response = await getCalendarAvailability({
      listingId,
      start: chunk.start,
      end: chunk.end,
    });
    Object.assign(days, response?.days || response?.data?.days || {});
  }

  return days;
}

function getRateSummary(daysMap, startYmd, endYmd) {
  const nightKeys = getStayNightKeys(startYmd, endYmd);
  let pricedNights = 0;
  let total = 0;

  nightKeys.forEach((dateKey) => {
    const rate = getNightlyRate(daysMap?.[dateKey]);
    if (rate !== null) {
      pricedNights += 1;
      total += rate;
    }
  });

  return {
    nights: nightKeys.length,
    pricedNights,
    average: pricedNights > 0 ? total / pricedNights : null,
    complete: nightKeys.length > 0 && pricedNights === nightKeys.length,
  };
}

function formatCurrencyNoCents(value) {
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(value));
}

function getPropertyName(property) {
  return property?.display_name || property?.clc_short_name || property?.name || property?.property_name || 'Selected property';
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function normalizeDateInputValue(value) {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) return formatDateKey(value);
  if (typeof value === 'string') return value.slice(0, 10);
  return '';
}

function trimFormValue(value) {
  return String(value ?? '').trim();
}

function getInitialFormValues({ initialStay, defaultCheckInDate, accountDefaults }) {
  const useAccountDefaults = !initialStay;
  const arrivalDate = normalizeDateInputValue(firstValue(
    initialStay?.check_in_date,
    initialStay?.checkin_date,
    initialStay?.arrival_date,
    initialStay?.arrivalDate,
    initialStay?.start_date,
    initialStay?.startDate,
    initialStay?.start,
    defaultCheckInDate,
    formatDateKey(new Date())
  ));
  const departureDate = normalizeDateInputValue(firstValue(
    initialStay?.check_out_date,
    initialStay?.checkout_date,
    initialStay?.departure_date,
    initialStay?.departureDate,
    initialStay?.end_date,
    initialStay?.endDate,
    initialStay?.end,
    addDaysYmd(arrivalDate, 1)
  ));

  return {
    arrivalDate,
    departureDate,
    ownerName: firstValue(
      initialStay?.guest_name,
      initialStay?.owner_name,
      initialStay?.guestName,
      useAccountDefaults ? accountDefaults?.ownerName : '',
      ''
    ),
    email: firstValue(
      initialStay?.email,
      useAccountDefaults ? accountDefaults?.email : '',
      ''
    ),
    phone: firstValue(
      initialStay?.phone,
      useAccountDefaults ? accountDefaults?.phone : '',
      ''
    ),
    numberOfGuests: String(firstValue(initialStay?.guest_count, initialStay?.number_of_guests, initialStay?.numberOfGuests, 1)),
    note: firstValue(initialStay?.note, initialStay?.notes, ''),
  };
}

export default function OwnerStayBookingModal({
  isOpen,
  property,
  defaultCheckInDate,
  accountDefaults = null,
  mode = 'create',
  initialStay = null,
  availability = null,
  onClose,
  onSubmit,
}) {
  const closeRef = useRef(null);
  const ownerNameRef = useRef(null);
  const [form, setForm] = useState({
    arrivalDate: '',
    departureDate: '',
    ownerName: '',
    email: '',
    phone: '',
    numberOfGuests: '1',
    note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedRangeAvailability, setSelectedRangeAvailability] = useState({});
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState(null);

  const isEditMode = mode === 'edit';
  const TitleIcon = isEditMode ? Pencil : CalendarPlus;
  const propertyName = getPropertyName(property);
  const listingId = property?.listing_id || property?.listingId || null;
  const nights = useMemo(
    () => dateDiffNights(form.arrivalDate, form.departureDate),
    [form.arrivalDate, form.departureDate]
  );
  const mergedAvailability = useMemo(
    () => ({
      ...(availability || {}),
      ...(selectedRangeAvailability || {}),
    }),
    [availability, selectedRangeAvailability]
  );
  const rateSummary = useMemo(
    () => getRateSummary(mergedAvailability, form.arrivalDate, form.departureDate),
    [mergedAvailability, form.arrivalDate, form.departureDate]
  );
  const averageNightlyRateText = useMemo(() => {
    if (nights <= 0) return '';
    if (rateSummary.average !== null) {
      const coverage = rateSummary.complete
        ? ''
        : ` (${rateSummary.pricedNights} of ${rateSummary.nights} nights)`;
      return `Avg listed nightly rate: ${formatCurrencyNoCents(rateSummary.average)}${coverage}`;
    }
    if (rateLoading) return 'Avg listed nightly rate: Loading...';
    if (rateError) return 'Selected dates not available';
    return 'Selected dates not available';
  }, [nights, rateError, rateLoading, rateSummary]);

  useEffect(() => {
    if (!isOpen) return;
    setForm(getInitialFormValues({ initialStay, defaultCheckInDate, accountDefaults }));
    setErrorMessage(null);
    setSubmitting(false);
    setSelectedRangeAvailability({});
    setRateLoading(false);
    setRateError(null);
  }, [isOpen, defaultCheckInDate, initialStay, mode]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedRangeAvailability({});
      setRateLoading(false);
      setRateError(null);
      return;
    }

    const nightKeys = getStayNightKeys(form.arrivalDate, form.departureDate);
    if (!listingId || nightKeys.length === 0) {
      setSelectedRangeAvailability({});
      setRateLoading(false);
      setRateError(null);
      return;
    }

    const alreadyLoaded = nightKeys.every((dateKey) => getNightlyRate(availability?.[dateKey]) !== null);
    if (alreadyLoaded) {
      setSelectedRangeAvailability({});
      setRateLoading(false);
      setRateError(null);
      return;
    }

    let cancelled = false;
    const startYmd = nightKeys[0];
    const endYmd = nightKeys[nightKeys.length - 1];

    setSelectedRangeAvailability({});
    setRateLoading(true);
    setRateError(null);

    fetchAvailabilityForStay({ listingId, startYmd, endYmd })
      .then((days) => {
        if (cancelled) return;
        setSelectedRangeAvailability(days);
        setRateLoading(false);
        setRateError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('[OwnerStayBookingModal] Error fetching selected stay rates:', error);
        setSelectedRangeAvailability({});
        setRateLoading(false);
        setRateError(error?.message || 'Rates unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [availability, form.arrivalDate, form.departureDate, isOpen, listingId]);

  useEffect(() => {
    if (!isOpen || isEditMode || initialStay) return;

    const fallbackArrivalDate = normalizeDateInputValue(defaultCheckInDate);
    setForm((current) => {
      const next = { ...current };
      let changed = false;

      if (fallbackArrivalDate && !next.arrivalDate) {
        next.arrivalDate = fallbackArrivalDate;
        changed = true;
      }
      if (fallbackArrivalDate && !next.departureDate) {
        next.departureDate = addDaysYmd(fallbackArrivalDate, 1);
        changed = true;
      }
      if (!next.ownerName && accountDefaults?.ownerName) {
        next.ownerName = accountDefaults.ownerName;
        changed = true;
      }
      if (!next.email && accountDefaults?.email) {
        next.email = accountDefaults.email;
        changed = true;
      }
      if (!next.phone && accountDefaults?.phone) {
        next.phone = accountDefaults.phone;
        changed = true;
      }

      return changed ? next : current;
    });
  }, [
    isOpen,
    isEditMode,
    initialStay,
    defaultCheckInDate,
    accountDefaults?.ownerName,
    accountDefaults?.email,
    accountDefaults?.phone,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = setTimeout(() => ownerNameRef.current?.focus(), 0);
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape' && !submitting) {
      onClose();
    }
  }, [onClose, submitting]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, isOpen]);

  if (!isOpen) {
    return null;
  }

  const updateField = (field, value) => {
    setErrorMessage(null);
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'arrivalDate' && YMD_PATTERN.test(value || '')) {
        const currentNights = dateDiffNights(value, next.departureDate);
        if (currentNights <= 0) {
          next.departureDate = addDaysYmd(value, 1);
        }
      }
      return next;
    });
  };

  const validate = () => {
    const ownerName = trimFormValue(form.ownerName);
    const email = trimFormValue(form.email);

    if (!property?.listing_id) return 'A property is required.';
    if (!ownerName) return 'Owner name is required.';
    if (!YMD_PATTERN.test(form.arrivalDate)) return 'Check-in date is required.';
    if (!YMD_PATTERN.test(form.departureDate)) return 'Check-out date is required.';
    if (nights <= 0) return 'Check-out must be after check-in.';
    const guests = Number(form.numberOfGuests);
    if (!Number.isFinite(guests) || guests < 1) return 'Number of guests must be at least 1.';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Email must be valid.';
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    try {
      await onSubmit({
        arrivalDate: form.arrivalDate,
        departureDate: form.departureDate,
        ownerName: trimFormValue(form.ownerName),
        email: trimFormValue(form.email),
        phone: trimFormValue(form.phone),
        numberOfGuests: Number(form.numberOfGuests),
        note: trimFormValue(form.note),
      });
    } catch (error) {
      setErrorMessage(error?.message || (isEditMode ? 'Owner stay could not be updated.' : 'Owner stay could not be booked.'));
      setSubmitting(false);
    }
  };

  const content = (
    <div style={styles.overlay} role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !submitting) onClose();
    }}>
      <form
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="owner-stay-title"
        onSubmit={handleSubmit}
      >
        <div style={styles.header}>
          <div style={styles.titleWrap}>
            <div style={styles.titleIcon} aria-hidden="true">
              <TitleIcon size={18} strokeWidth={2.25} />
            </div>
            <div>
              <h2 id="owner-stay-title" style={styles.title}>{isEditMode ? 'Edit Owner Stay' : 'Book Owner Stay'}</h2>
              <p style={styles.subtitle}>{propertyName}</p>
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            style={styles.iconButton}
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div style={styles.body}>
          {errorMessage && (
            <div style={styles.errorBanner} role="alert">
              {errorMessage}
            </div>
          )}

          <div style={styles.grid}>
            <label style={styles.field}>
              <span style={styles.label}>Owner name</span>
              <input
                ref={ownerNameRef}
                type="text"
                value={form.ownerName}
                onChange={(event) => updateField('ownerName', event.target.value)}
                style={styles.input}
                autoComplete="name"
                required
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Guests</span>
              <input
                type="number"
                min="1"
                max="99"
                value={form.numberOfGuests}
                onChange={(event) => updateField('numberOfGuests', event.target.value)}
                style={styles.input}
                required
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Check-in</span>
              <input
                type="date"
                value={form.arrivalDate}
                onChange={(event) => updateField('arrivalDate', event.target.value)}
                style={styles.input}
                required
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Check-out</span>
              <input
                type="date"
                value={form.departureDate}
                onChange={(event) => updateField('departureDate', event.target.value)}
                style={styles.input}
                required
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Phone</span>
              <input
                type="tel"
                value={form.phone}
                onChange={(event) => updateField('phone', event.target.value)}
                style={styles.input}
                autoComplete="tel"
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(event) => updateField('email', event.target.value)}
                style={styles.input}
                autoComplete="email"
              />
            </label>
          </div>

          <label style={styles.field}>
            <span style={styles.label}>Notes</span>
            <textarea
              value={form.note}
              onChange={(event) => updateField('note', event.target.value)}
              style={styles.textarea}
              rows={3}
            />
          </label>
        </div>

        <div style={styles.footer}>
          <div style={styles.staySummary}>
            <div style={styles.nights}>{nights > 0 ? `${nights} night${nights === 1 ? '' : 's'}` : ''}</div>
            {averageNightlyRateText && (
              <div
                style={{
                  ...styles.averageRate,
                  ...(averageNightlyRateText === 'Selected dates not available' ? styles.unavailableRate : {}),
                }}
              >
                {averageNightlyRateText}
              </div>
            )}
          </div>
          <div style={styles.actions}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.primaryButton,
                opacity: submitting ? 0.72 : 1,
              }}
              disabled={submitting}
            >
              {submitting
                ? (isEditMode ? 'Saving...' : 'Booking...')
                : (isEditMode ? 'Save Changes' : 'Book Owner Stay')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(content, document.body)
    : content;
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: 'rgba(17, 24, 39, 0.12)',
  },
  modal: {
    width: 'min(640px, 100%)',
    maxHeight: 'calc(100vh - 40px)',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(255, 255, 255, 0.68)',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: '8px',
    boxShadow: '0 20px 45px rgba(15, 23, 42, 0.24)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '20px 24px',
    borderBottom: '1px solid var(--color-border-light, #e5e7eb)',
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  },
  titleIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
    color: 'var(--color-brand-primary, #2563eb)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    color: 'var(--color-text-primary, #111827)',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  subtitle: {
    margin: '4px 0 0',
    color: 'var(--color-text-secondary, #6b7280)',
    fontSize: '14px',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  iconButton: {
    width: '40px',
    height: '40px',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    color: 'var(--color-text-secondary, #6b7280)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  body: {
    padding: '24px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  errorBanner: {
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid #fecaca',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    fontSize: '14px',
    lineHeight: 1.4,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0,
  },
  label: {
    color: 'var(--color-text-secondary, #374151)',
    fontSize: '13px',
    fontWeight: 600,
  },
  input: {
    width: '100%',
    minHeight: '42px',
    boxSizing: 'border-box',
    padding: '9px 11px',
    border: '1px solid var(--color-border-medium, #d1d5db)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    color: 'var(--color-text-primary, #111827)',
    fontSize: '15px',
  },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 11px',
    border: '1px solid var(--color-border-medium, #d1d5db)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    color: 'var(--color-text-primary, #111827)',
    fontSize: '15px',
    resize: 'vertical',
    minHeight: '88px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '18px 24px',
    borderTop: '1px solid var(--color-border-light, #e5e7eb)',
    flexWrap: 'wrap',
  },
  staySummary: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  nights: {
    color: 'var(--color-text-secondary, #6b7280)',
    fontSize: '14px',
    minHeight: '20px',
  },
  averageRate: {
    color: 'var(--color-text-primary, #111827)',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.35,
  },
  unavailableRate: {
    color: '#991b1b',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexShrink: 0,
    marginLeft: 'auto',
  },
  secondaryButton: {
    minHeight: '42px',
    padding: '0 16px',
    border: '1px solid var(--color-border-medium, #d1d5db)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    color: 'var(--color-text-primary, #111827)',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  primaryButton: {
    minHeight: '42px',
    padding: '0 18px',
    border: '1px solid var(--color-brand-primary, #2563eb)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-brand-primary, #2563eb)',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};
